import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { WizardStepProps } from '../PowerpointExportWizard';
import { useLocation, useParams } from 'react-router-dom';
import {
  selectPptExportables,
  setWizardStep,
  toggleExportable,
} from 'common/store/slices/export-ppt.slice';
import { selectExportByWidgetType, selectExportData } from 'common/store/slices/export.slice';
import { PAGINATED_PPT_EXPORT_ROUTES } from 'modules/insights/constants';
import { useState } from 'react';
import { RootState } from 'common/store';
import { WidgetID, WidgetType } from 'common/models/widget.model';
import { showToast } from 'common/store/slices/notification.slice';
import { NotificationType } from 'common/models/notification.model';
import { FlyoutBase, FlyoutBody } from '../Flyout';
import { KWELLO_ICON } from 'common/components/icon/KwelloSvgIcon';
import { ExportModel } from 'common/models/export.model';
import { Typography } from '@progress/kendo-react-common';
import { AppliedFilters } from 'common/components/layouts/filters/AppliedFilters';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import Footer from './Footer';
import { FlyoutHeader } from '../components/FlyoutHeader';
import { USER_ROUTES } from 'routes/routes.const';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import useNavigationMenus from 'common/hooks/useMenus';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature } from 'common/models/page-config.models';
import { DATASET_INITIAL_ID, PROJECT_ID_PLACEHOLDER } from 'common/constants';
import { usePageExportPPT } from '../../widgets/widget-hooks/useClientSideExportPPT';
import { useSelector } from 'react-redux';
import { selectDatasetQuestion } from '../../../store/slices/dataset.slice';
import { selectWidgetOptions } from '../../../store/slices/widget-config.slice';
import { useSendSummarizeMutation } from '../../../store/api/elsie.api';
import { castToType } from '../../../hooks/utils';
import { WidgetExportModel } from '../../../models/export.model';
import { selectPageName, selectPageTitle } from '../../../store/slices/layout.slice';
import { selectElsieSummaryData } from '../../../store/slices/elsie-ai.slice';
import useApiLoadingState from '../../../hooks/useApiLoadingState';
import { WizardStep } from '../../../models/pptx.model';

export function WizardSummary({ onClose }: Readonly<WizardStepProps>) {
  const dispatch = useAppDispatch();
  const { hasElsieMenuItem } = useNavigationMenus();
  const { pathname } = useLocation();
  const { projectId = '' } = useParams();
  const { hasFeature } = useFeatureFlags();
  const exportables = useAppSelector(selectPptExportables);
  const exportState = useAppSelector(selectExportData);
  const { compareUrl } = useCompareUrlWithProjectId();
  const { exportPagePPT } = usePageExportPPT();
  const widgetOptions = useSelector((state: RootState) => selectWidgetOptions(state, projectId));
  const datasetQuestion = useSelector((state: RootState) =>
    selectDatasetQuestion(state, DATASET_INITIAL_ID, projectId)
  );
  const pageTitle = useSelector(selectPageTitle);
  const pageName = useSelector(selectPageName);

  const hasSummaryButton = hasFeature(Feature.ElsieAiSummaryButton);
  const exportableKeys = Object.keys(exportables).filter((key) =>
    key === 'ai-chat' ? hasElsieMenuItem && hasSummaryButton : true
  );
  const elsieSummary = useAppSelector(selectElsieSummaryData);
  const successToast = {
    id: 'ppt-export-notification',
    text: `Your Powerpoint is now downloading. Once completed,
          <br />
          you can access it from 'My Files' page`,
    hasButton: true,
    buttonLabel: 'View',
    buttonLink: USER_ROUTES.files.replace(PROJECT_ID_PLACEHOLDER, projectId),
    type: NotificationType.INFO,
    newTab: true,
  };

  const errorToast = {
    id: 'ppt-export-notification',
    text: `Error generating your PowerPoint. Please try again. If the error persists, please contact
support@kwello.com`,
    hasButton: false,
    type: NotificationType.ERROR,
  };

  const isElsieAiChatExport = Object.keys(exportables).includes('ai-chat');

  const [sendSummarize] = useSendSummarizeMutation();

  const isPaginatedPptExport = compareUrl(PAGINATED_PPT_EXPORT_ROUTES, { includes: true });
  const isDetailPage = pathname.endsWith('detail');

  const [isLoading, setIsLoading] = useState(false);

  const { chartApiLoadingState } = useApiLoadingState();

  const { failedWidgetIds } = chartApiLoadingState;

  const summaryBar = useAppSelector((state: RootState) =>
    selectExportByWidgetType(state, WidgetType.COUNTER)
  );
  const isExportDisabled = !exportableKeys.some(
    (key) =>
      exportables[key]?.state === true && !!exportState[key] && !failedWidgetIds.includes(key)
  );

  const onExportableStateChange = (id: WidgetID, state: boolean) => {
    dispatch(
      toggleExportable({
        id,
        state,
      })
    );
  };

  const exportMultiple = async () => {
    const exportData: ExportModel[] = [];
    exportableKeys.forEach((key: string) => {
      const exportableState = exportables[key];
      if (exportableState.state === true && exportState[key] && !failedWidgetIds.includes(key)) {
        exportData.push(exportState[key] as ExportModel);
      }
    });
    if (exportData.length > 0) {
      setIsLoading(true);
      try {
        if (isElsieAiChatExport && hasSummaryButton) {
          const elsieWidget = exportData.find(
            (data) => data?.widget?.type === WidgetType.ELSIE_AI_EXPORT
          );
          if (elsieWidget) {
            const result = await sendSummarize({
              ...elsieWidget?.response?.elsieAiSummary,
            });
            if (result.data?.chatResponse) {
              exportData.unshift({
                ...elsieWidget,
                response: {
                  summary: castToType<WidgetExportModel>(result.data),
                  citationsData: castToType<WidgetExportModel>(elsieSummary?.referenceData),
                },
              });
            }
            exportData.splice(exportData.length - 1, 1);
          }
        }

        await exportPagePPT({
          widgets: exportData,
          question: datasetQuestion,
          summaryBar,
          pageTitle,
          pageName,
          widgetOptions,
        });
        dispatch(showToast(successToast));
      } catch (e) {
        console.error('Error exporting PPT:', e);
        dispatch(showToast(errorToast));
      } finally {
        setIsLoading(false);
        onClose();
      }
    }
  };

  return (
    <FlyoutBase rootClassName="flyout-paginated-ppt-export" onOverlayClick={onClose}>
      <FlyoutHeader
        title={'Page Export'}
        onCloseClick={onClose}
        icon={isPaginatedPptExport ? KWELLO_ICON.BACK_ICON : undefined}
        onLeftIconClick={() => dispatch(setWizardStep(WizardStep.PowerpointExportChooseOption))}
      />
      <FlyoutBody>
        <Typography.p className="heading">
          Please select the information that you want to include in your export to Power Point.
        </Typography.p>

        <div className="flyout-filter-container">
          <Typography.h6>Applied filters</Typography.h6>
          <div className="filter-disclaimer">
            <span className="filter-disclaimer-title">
              These filters will shape your PowerPoint export.
            </span>

            <span className="filter-disclaimer-body">
              Want to tweak them? Just hop back to the {isDetailPage ? 'Detail' : 'Summary'} page
              and adjust your filters
            </span>
          </div>

          <AppliedFilters showTooltip={false} removable={false} textClass="gray-700" />
        </div>

        {exportableKeys.map((exportable: string) => {
          const exportableState = exportables[exportable];
          return (
            <div key={exportable} className="exportable-row">
              <div className="exportable-toggle">
                {' '}
                <Checkbox
                  onChange={(e: CheckboxChangeEvent) =>
                    onExportableStateChange(exportable, e.value)
                  }
                  defaultChecked={exportableState.state}
                  label={'Include ' + exportableState.displayName}
                  disabled={isLoading || failedWidgetIds.includes(exportable)}
                />
              </div>
            </div>
          );
        })}
      </FlyoutBody>
      <Footer onExport={exportMultiple} isLoading={isLoading} disabled={isExportDisabled} />
    </FlyoutBase>
  );
}

export default WizardSummary;
